import { WorkawareApi } from 'apis/WorkawareApi'

export interface UserDto {
  id: number
  firstName: string
  lastName: string
  email: string
  username?: string
  isActive: boolean
  includeInCertificateVerification: boolean
}

export interface CourseDto {
  id: number
  name: string
  isRequired: boolean
  notifyDaysInAdvance: number
  createdAt?: string
}

export interface UserExemptedCertificateDto {
  id: number
  userId: number
  courseId: number
  companyId: number
}

export interface UserCompletedCertificateDto {
  id: number
  userId: number
  courseId: number
  certifiedOn?: string
  certificateExpiration?: string
  name: string
  instructor?: string
}

export class trainingMatrixConfigurationService {
  private _api: WorkawareApi

  constructor(api: WorkawareApi) {
    this._api = api
  }

  // Get id→name map for all users (including inactive) so cert drill-downs can display names
  getUserNameMap = (): Promise<Record<number, string>> =>
    this._api.user.all().then((r: any) => {
      const map: Record<number, string> = {}
      ;(r?.value ?? r ?? []).forEach((u: any) => {
        const id = Number(u?.Id ?? u?.id)
        if (!isNaN(id)) {
          const name =
            u?.BestName || u?.bestName ||
            [u?.PreferredName || u?.Firstname, u?.Lastname].filter(Boolean).join(' ') ||
            u?.WorkEmail || u?.PersonalEmail || u?.Email || u?.email ||
            u?.Username || u?.username || ''
          map[id] = name || `User #${id}`
        }
      })
      return map
    })

  // Get all active users that should be included in certificate verification
  getActiveUsers = (): Promise<UserDto[]> => {
    return new Promise((resolve, reject) => {
      this._api.user
        .all()
        .then((result) => {
          const filtered = (result.value || []).filter(
            (u: any) => u.IsActive
          )
          const mapped: UserDto[] = filtered.map((u: any) => ({
            id: u.Id,
            firstName: u.Firstname,
            lastName: u.Lastname,
            email: u.WorkEmail || u.PersonalEmail || '',
            username: u.Username || u.username || '',
            isActive: u.IsActive,
            includeInCertificateVerification: u.IncludeInCertificateVerification,
          }))
          resolve(mapped.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)))
        })
        .catch((error) => reject(error))
    })
  }

  // Get required courses for the company (used by the certification compliance matrix,
  // which only tracks users against required certifications)
  getCourses = (): Promise<CourseDto[]> => {
    return this.getAllCourses().then(courses => courses.filter(c => c.isRequired))
  }

  // Get every course/certification for the company, required and general alike.
  // Used by reporting so totals match Certification Management (which lists all courses).
  getAllCourses = (): Promise<CourseDto[]> => {
    return new Promise((resolve, reject) => {
      this._api.courses
        .get()
        .then((result) => {
          const mapped: CourseDto[] = (result.value || result || []).map((c: any) => ({
            id: c.Id,
            name: c.Name,
            isRequired: c.IsRequired,
            notifyDaysInAdvance: c.NotifyDaysInAdvance ?? 30,
            createdAt: c.Created ?? c.CreatedOn ?? c.CreatedAt ?? c.DateCreated,
          }))
          resolve(mapped.sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()))
        })
        .catch((error) => reject(error))
    })
  }

  // Get all exempted certificates for the company
  getExemptedCertificates = (): Promise<UserExemptedCertificateDto[]> => {
    return new Promise((resolve, reject) => {
      this._api.userExemptedCertificates
        .get()
        .then((result) => {
          const mapped: UserExemptedCertificateDto[] = (result.value || result || [])
            .filter((e: any) => !e.IsDeleted)
            .map((e: any) => ({
              id: e.Id,
              userId: e.UserId,
              courseId: e.CourseId,
              companyId: e.CompanyId,
            }))
          resolve(mapped)
        })
        .catch((error) => reject(error))
    })
  }

  // Get all completed certificates for the company
  getCompletedCertificates = (): Promise<UserCompletedCertificateDto[]> => {
    return new Promise((resolve, reject) => {
      this._api.userCompletedCertificates
        .get()
        .then((result) => {
          const mapped: UserCompletedCertificateDto[] = (result.value || result || []).map((c: any) => ({
            id: c.Id,
            userId: c.UserId,
            courseId: c.CourseId,
            certifiedOn: c.CertifiedOn,
            certificateExpiration: c.CertificateExpiration,
            name: c.Name,
            instructor: c.Instructor,
          }))
          resolve(mapped)
        })
        .catch((error) => reject(error))
    })
  }

  // Create a new exemption
  createExemption = (userId: number, courseId: number): Promise<UserExemptedCertificateDto> => {
    return new Promise((resolve, reject) => {
      const exemption = {
        UserId: userId,
        CourseId: courseId,
      }
      this._api.userExemptedCertificates
        .post(exemption)
        .then((result) => {
          resolve({
            id: result.Id,
            userId: result.UserId,
            courseId: result.CourseId,
            companyId: result.CompanyId,
          })
        })
        .catch((error) => reject(error))
    })
  }

  // Delete an exemption
  deleteExemption = (exemptionId: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      console.log(`[DELETE] Attempting to delete exemption ID: ${exemptionId}`)
      this._api.userExemptedCertificates
        .delete(exemptionId)
        .then(() => {
          console.log(`[DELETE] Successfully deleted exemption ID: ${exemptionId}`)
          resolve()
        })
        .catch((error) => {
          console.error(`[DELETE] Failed to delete exemption ID ${exemptionId}:`, error)
          if (error?.status === 404) {
            console.log(`[DELETE] Exemption ${exemptionId} not found (404) - treating as success`)
            resolve()
          } else {
            reject(error)
          }
        })
    })
  }

  // Batch operations for better performance
  syncExemptions = (
    toCreate: Array<{ userId: number; courseId: number }>,
    toDelete: number[]
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Create new exemptions
        for (const exemption of toCreate) {
          try {
            await this.createExemption(exemption.userId, exemption.courseId)
          } catch (error) {
            console.error(`Failed to create exemption for user ${exemption.userId}, course ${exemption.courseId}:`, error)
            throw error
          }
        }

        // Delete removed exemptions
        for (const exemptionId of toDelete) {
          try {
            await this.deleteExemption(exemptionId)
          } catch (error) {
            console.error(`Failed to delete exemption with ID ${exemptionId}:`, error)
            throw error
          }
        }

        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }
}
